import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';
import AuraLogo from '@trinity/chat-components/static/images/aura-icon.svg?url';

const PRIMARY = '#e4d9f4';

const SECONDARY = '#9c7fd4';

const DOT_COUNT = 12;
const DOT_RADIUS = 82; // px from scene center to dot center
const DURATION = 1.6; // seconds per full animation cycle

const dotPulse = keyframes`
  0%, 100% {
    opacity: 0.25;
    transform: translate(-50%, -50%) scale(0.65);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.5);
  }
`;

const logoAura = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.55;
  }
  100% {
    transform: translate(-50%, -50%) scale(2.1);
    opacity: 0;
  }
`;

export function DigitalTwinLoader({ compact = false }: { compact?: boolean }) {
  const dots = Array.from({ length: DOT_COUNT }, (_, i) => {
    const angle = (i / DOT_COUNT) * 2 * Math.PI;
    const x = Math.sin(angle) * DOT_RADIUS;
    const y = -Math.cos(angle) * DOT_RADIUS;
    // Negative delay so animation starts mid-cycle — immediate staggered wave on mount
    const delay = (i / DOT_COUNT) * DURATION - DURATION;
    // Every 3rd dot gets the darker secondary colour
    const color = i % 3 === 0 ? SECONDARY : PRIMARY;
    return { x, y, delay, color };
  });

  return (
    <Backdrop compact={compact}>
      <Scene>
        {dots.map(({ x, y, delay, color }, i) => (
          <Dot
            key={i}
            style={{
              left: `calc(50% + ${x}px)`,
              top: `calc(50% + ${y}px)`,
              animationDelay: `${delay}s`,
              backgroundColor: color,
            }}
          />
        ))}
        <LogoCircle>
          <img src={AuraLogo} alt="Loading Digital Twin" width={46} height={46} />
        </LogoCircle>
      </Scene>
    </Backdrop>
  );
}

const Backdrop = styled('div', { shouldForwardProp: shouldForwardProp('compact') })<{ compact: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  ${({ compact }) => !compact && 'min-height: 400px;'}
`;

const Scene = styled.div`
  position: relative;
  width: 210px;
  height: 210px;
`;

const Dot = styled.div`
  position: absolute;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  /* translate(-50%, -50%) centres the dot on its computed left/top position */
  animation: ${dotPulse} ${DURATION}s ease-in-out infinite;
`;

const LogoCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 78px;
  height: 78px;
  border-radius: 50%;
  background-color: ${SECONDARY};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 0 18px 4px rgba(156, 127, 212, 0.45),
    0 0 40px 8px rgba(156, 127, 212, 0.2);

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 78px;
    height: 78px;
    border-radius: 50%;
    background-color: ${SECONDARY};
    opacity: 0;
    animation: ${logoAura} 2.4s ease-out infinite;
  }

  &::after {
    animation-delay: 1.2s;
  }
`;
